<script setup>
import { computed, useSlots } from "vue";
import { X } from "lucide-vue-next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/ui/dialog";
import { cn } from "@/shared/lib/utils";

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, default: "" },
  description: { type: String, default: "" },
  width: { type: [String, Number], default: 480 },
  showFooter: { type: Boolean, default: true },
  showClose: { type: Boolean, default: true },
  closeOnOutside: { type: Boolean, default: true },
  contentClass: {
    type: [String, Object, Array],
    default: undefined,
  },
});

const emit = defineEmits(["update:open", "close"]);
const slots = useSlots();

const computedWidth = computed(() => {
  if (typeof props.width === "number") return `${props.width}px`;
  if (/^\d+$/.test(String(props.width))) return `${props.width}px`;
  return props.width;
});

function handleOpenChange(value) {
  emit("update:open", value);
  if (!value) emit("close");
}

function onOutside(event) {
  if (!props.closeOnOutside) event.preventDefault();
}

function close() {
  handleOpenChange(false);
}

defineExpose({
  open: () => emit("update:open", true),
  close,
});
</script>

<template>
  <Dialog :open="open" @update:open="handleOpenChange">
    <DialogContent
      :style="{ maxWidth: computedWidth }"
      :class="
        cn(
          'p-0 flex flex-col gap-0 sm:max-w-none [&>button]:hidden border border-solid border-border shadow-lg overflow-hidden',
          contentClass
        )
      "
      @pointer-down-outside="onOutside"
      @interact-outside="onOutside"
    >
      <DialogHeader class="sr-only">
        <DialogTitle>{{ title }}</DialogTitle>
        <DialogDescription>{{ description || title }}</DialogDescription>
      </DialogHeader>

      <slot name="header">
        <div class="fd-header">
          <slot name="header-extra" />
          <div class="title">{{ title }}</div>
          <button
            v-if="showClose"
            type="button"
            class="fd-close"
            @click="close"
          >
            <X :size="14" />
          </button>
        </div>
      </slot>

      <div class="fd-body">
        <slot />
      </div>

      <div v-if="showFooter && slots.footer" class="fd-footer">
        <slot name="footer" />
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped lang="scss">
.fd-header {
  @apply flex items-center justify-between py-3.5 px-6 bg-muted border-0 border-b border-solid border-border flex-shrink-0;

  .title {
    @apply font-medium text-black text-start w-full;
    font-size: 1.1rem;
  }
}

.fd-close {
  @apply w-6 h-6 text-muted-foreground flex-shrink-0 flex items-center justify-center rounded-md bg-white border border-solid border-border cursor-pointer p-0;

  &:hover {
    opacity: 0.8;
  }
}

.fd-body {
  @apply flex flex-col gap-3 px-6 py-5 flex-1 overflow-y-auto max-h-[80vh];
}

.fd-footer {
  @apply flex items-center gap-2 px-6 py-2.5 border-0 border-t border-solid border-border bg-muted flex-shrink-0;
}
</style>
